import {
  Component,
  OnInit,
  AfterViewInit,
  ElementRef,
  ViewEncapsulation,
  OnDestroy,
  HostBinding,
  ViewChild,
  Inject,
  Optional,
  EventEmitter,
  Output,
  HostListener,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { interval, merge, of, Subject, Subscription, zip } from 'rxjs';
import { takeUntil, map, pairwise, filter, first } from 'rxjs/operators';
import { CrossBrowsing } from '../constants/cross-browsing';

import { Address, BookType, PageInfoChangeEvent, Theme } from '@bukio/viewer';
import { setTransport, flush } from '@amplitude/analytics-browser';

import {
  DialogService,
  SUI_DIALOG_DEFAULT_OPTIONS,
  ToastService,
} from 'shared/ui';
import {
  AmplitudeService,
  ReadingGroupsAPIService,
  SharedPageTitleService,
  SharedUserService,
} from 'shared/services';
import { roundTo } from 'shared/utils';

import { VIEWER_ENVIRONMENT, ViewerModuleEnvironment } from '../viewer.module';

import { logAffiliationToCookie } from '../utils/address';
import { isViewerEmbedded } from '../utils/misc';

import { Book } from '../models/book.model';
import {
  ReadingGroupWithMyActivityStats,
  ReadingGroupJoinStatus,
} from 'shared/models';

import { IntervalLoggerService } from '../services/interval-logger.service';
import { EventBusService } from '../services/event-bus.service';
import { BukHistoryService } from '../services/buk-history.service';
import { UIStateService } from '../services/ui-state.service';
import { AnnotationsV2Service } from '../services/annotations-v2.service';
import { ReadingGroupsService } from '../services/reading-groups.service';
import { BookService } from '../services/book.service';
import { MemoReadStatusService } from '../services/memo-read-status.service';
import { OnlineReadingMemberService } from '../services/online-reading-member.service';

import { SidebarContainerComponent } from '../components/sidebar-container/sidebar-container.component';
import { MemoPopupComponent } from '../components/memo-popup/memo-popup.component';
import { RightPanelComponent } from '../components/right-panel/right-panel.component';
import { StaticMemoPopupComponent } from '../components/static-memo-popup/static-memo-popup.component';
import { Dialog } from '@angular/cdk/dialog';

import { HeaderMenuItem } from '../components/header/header.component';
import { FooterMenuItem } from '../components/footer/footer.component';
import { ContentsComponent } from '../components/contents/contents.component';

@Component({
  selector: 'viewer-root',
  templateUrl: './buk.component.html',
  styleUrls: ['./buk.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    DialogService,
    UIStateService,
    IntervalLoggerService,
    AnnotationsV2Service,
    ReadingGroupsService,
    OnlineReadingMemberService,
    MemoReadStatusService,
    BookService,
    {
      provide: SUI_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        responsiveBreakpoint: 768,
        dimmed: true,
      },
    },
  ],
})
export class BukComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('attr.data-theme') _theme?: Theme;
  @ViewChild('verticalSidebarContainer', { read: SidebarContainerComponent })
  _verticalSidebarContainerComp!: SidebarContainerComponent;

  @ViewChild(ContentsComponent) _contentsComp!: ContentsComponent;

  @ViewChild(MemoPopupComponent, { static: true })
  private _memoPopupComp!: MemoPopupComponent;
  @ViewChild(StaticMemoPopupComponent, { static: true })
  private _staticMemoPopupComp!: StaticMemoPopupComponent;
  @ViewChild(RightPanelComponent, { static: true })
  private _rightPanelComp!: RightPanelComponent;

  @ViewChild('sidebarTop', { static: true })
  private _sidebarTop!: ElementRef;
  @ViewChild('sidebarMobileBottom', { static: true })
  private _sidebarMobileBottom!: ElementRef;
  @ViewChild('sidebarDesktopBottom', { static: true })
  private _sidebarDesktopBottom!: ElementRef;
  @ViewChild('rightSidebar', { static: false })
  private _rightSidebar!: ElementRef<HTMLElement>;

  @ViewChild('rightSidebarContainer', { read: SidebarContainerComponent })
  private _rightSidebarContainerComp!: SidebarContainerComponent;

  @Output() menuOpenedChange = new EventEmitter<boolean>();
  @Output() bookLoad = new EventEmitter<Book>();

  @HostListener(`window:pagehide`, [])
  pageHide(): void {
    const navType = performance.getEntriesByType(
      'navigation'
    )[0] as PerformanceNavigationTiming;

    if (navType.type !== 'reload') {
      setTransport('beacon');
      this._callExitAmplitudeEvent(this._readingGroup);
      flush();
    }
  }

  @HostListener('window:resize', [])
  onWindowResize(): void {
    // 데스크탑 모드로 전환될 때 rightSidebar의 height 리셋
    if (window.innerWidth >= 1200 && this._rightSidebar?.nativeElement) {
      // 모바일에서 설정된 height 제거
      this._rightSidebar.nativeElement.style.height = '';
      this._bottomSheetHeight = 0;
      // 뷰어 높이도 리셋
      this._updateViewerHeight();
    }
  }

  private unsubscribeBroadcast: Subject<void> = new Subject<void>();
  private book?: Book;

  public _isMenuOpened = false;
  private _embeddedSlideMode = false;
  private _isSidebarOpened = false;

  private _groupId?: string;

  private _readingGroup: ReadingGroupWithMyActivityStats | null = null;
  private _completionRate?: number;
  private _selectedCommentaryAuthor?: string;

  public expiredTime?: string; // 만료 시간
  public isExpired: boolean = false; // 현재시간 대비 만료시간이 지나 만료됐는지 판단하는 값
  private intervalSub?: Subscription;

  private sidebars: { [key: string]: HTMLElement } = {};

  public isMobile = CrossBrowsing.touchDevice;
  private _bottomSheetHeight = 0; // 기본 높이 (body 높이의 1/3로 초기화)
  private _isDragging = false;
  private _startY = 0;
  private _startHeight = 0;
  private _minHeight = 100; // 최소 높이
  private _maxHeight = window.innerHeight * 0.9; // 최대 높이 (화면의 90%)

  private _isInitialized = false;
  private _isInitializing = false; // 초기화 진행 중 플래그 추가
  private _wasMultiColumnBeforeRightPanelOpen?: boolean; // right-panel 열기 전 두 페이지보기 모드 상태 저장
  private _hasAttemptedAutoJoin = false; // 자동 그룹 가입 시도 여부 플래그

  // 댓글 입력 중 알림 팝업 관련
  public isCommentInputActive = false;
  public selectedCommentText = '';

  // 답글 입력 중 알림 팝업 관련
  public isReplyInputActive = false;
  public selectedReplyNickname = '';

  private _userClose = false;
  private _skipHighlightsOnNextPanelOpen = false;
  private _suppressHighlightOnNextModeSwitch = false;

  constructor(
    private element: ElementRef,
    private router: Router,
    private activateRoute: ActivatedRoute,
    private eventBusService: EventBusService,
    private intervalLoggerService: IntervalLoggerService,
    private bukHistoryService: BukHistoryService,
    private uiStateService: UIStateService,
    @Inject(VIEWER_ENVIRONMENT) private _environment: ViewerModuleEnvironment,
    private _toastService: ToastService,
    private _amplitudeService: AmplitudeService,
    private _readingGroupsService: ReadingGroupsService,
    private _readingGroupsAPIService: ReadingGroupsAPIService,
    onlineReadingMemberService: OnlineReadingMemberService,
    bookService: BookService,
    private _readingGroupAPIService: ReadingGroupsAPIService,
    private dialog: Dialog,
    @Optional() private _pageTitleService?: SharedPageTitleService,
    @Optional() private _userService?: SharedUserService
  ) {
    this._groupId = this.activateRoute.snapshot.queryParams['gId'];
  }

  ngOnInit(): void {
    if (this._groupId) {
      this._readingGroupsAPIService.touch(Number(this._groupId)).subscribe();
    }

    this._readingGroupsService.currentGroup$.subscribe((group) => {
      this._readingGroup = group;
    });

    document.body.classList.add('viewer-fixed');

    this._environment.production && this.intervalLoggerService.start();

    if (isViewerEmbedded()) {
      this.element.nativeElement.style.border = '1px solid #e0e0e0';
    }

    merge(
      this.eventBusService.on('ContentsComponent:bookLoadError'),
      this.eventBusService.on('ContentsComponent:itemLoadError')
    )
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe(() => {
        this._toastService
          .openWarning('책 주소가 유효하지 않습니다.', '홈으로')
          .onAction()
          .subscribe(() => {
            this.router.navigateByUrl('/');
          });
      });

    this.eventBusService
      .on('ContentsComponent:settingsChanged')
      .pipe(
        takeUntil(this.unsubscribeBroadcast),
        map((data) => data.settings.theme.value)
      )
      .subscribe((theme) => {
        setTimeout(() => {
          this._theme = theme;
          document.body.setAttribute('data-theme', theme);
        }, 0);
      });

    this.eventBusService
      .on('ContentsComponent:bookLoad')
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe(({ book }) => {
        if (!book.meta.sibf) {
          this.isExpired = true;
        } else {
          const expiredAt = book.meta.sibf.expired_at;

          // 현재 티켓이 만료 됐는지 판단
          if (new Date() >= new Date(expiredAt)) {
            this.isExpired = true;
          } else {
            // 만료되지 않았다면 카운트 다운 시작
            this.intervalSub = interval(1000).subscribe(() => {
              this.updateCountdown(expiredAt, book.meta.bid);
            });
          }
        }

        if (this._groupId) {
          this._readingGroupsAPIService
            .getById(Number(this._groupId))
            .subscribe((result) => {
              this._callEnterAmplitudeEvent(result, book);
              // 그룹 정보를 가져온 후 자동 가입 시도
              this._tryAutoJoinGroup(book, result);
            });
        } else {
          this._callEnterAmplitudeEvent(null, book);
        }

        this.book = book;

        this._embeddedSlideMode = isViewerEmbedded() && book.isHorizontalPDF;

        this._pageTitleService?.setPageTitle(book.meta.title);

        if (this.book.permissionReferer) {
          logAffiliationToCookie(this.book.permissionReferer);
        }

        this.bookLoad.emit(book);

        // right-panel이 열려있을 때 두 페이지 보기를 한 페이지 보기로 변경
        // 약간의 지연을 두어 패널 상태가 초기화된 후 확인
        setTimeout(() => {
          const isRightPanelOpened =
            this._rightPanelComp &&
            this._rightSidebarContainerComp?.isOpened?.('right');

          if (
            !isRightPanelOpened ||
            !this._contentsComp?.getCurrentSettings()
          ) {
            return;
          }

          // 그룹 모드: comment 패널, 나의책 모드: myAnnotations 패널
          const isCommentPanelOpened = this._rightPanelComp.isOpened('comment');
          const isMyAnnotationsPanelOpened =
            this._rightPanelComp.isOpened('myAnnotations');

          if (isCommentPanelOpened || isMyAnnotationsPanelOpened) {
            const message = isCommentPanelOpened
              ? '댓글 보기를 위해 한 페이지 보기 모드로 변경합니다.'
              : '내 기록 보기를 위해 한 페이지 보기 모드로 변경합니다.';
            this._toastService.open(message);

            this._wasMultiColumnBeforeRightPanelOpen = true;
            this._contentsComp?._onSettingsChanged('multiColumn', false);
          }
        }, 200);

        // 책이 로드된 후 뷰어 높이 업데이트 (PDF 렌더링 전에 높이 조정)
        // 초기화가 아직 완료되지 않았고, 진행 중도 아니면 초기화 실행
        if (this.isMobile && !this._isInitialized && !this._isInitializing) {
          this._isInitializing = true;
          setTimeout(() => {
            this._checkInitialBottomSheetState();
            this._isInitialized = true;
            this._isInitializing = false;
          }, 100);
        } else if (this.isMobile && this._isInitialized) {
          // 이미 초기화가 완료된 경우에만 업데이트
          setTimeout(() => {
            this._checkInitialBottomSheetState();
          }, 100);
        }
      });

    this.eventBusService
      .on('ContentsComponent:pageClick')
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe(() => {
        // 데스크탑에서도 토글 동작하도록 수정
        // if (window.innerWidth < 1200) {
        if (this._isMenuOpened) {
          this._closeMenu();
        } else {
          this._openMenu();
        }
        // }
      });

    this.eventBusService
      .on('ContentsComponent:addressChange')
      .pipe(
        takeUntil(this.unsubscribeBroadcast),
        map((data) => data.address),
        pairwise(),
        filter(([addr1, addr2]) => {
          return (
            addr1.bid !== addr2.bid ||
            addr1.iid !== addr2.iid ||
            addr1.anchor !== addr2.anchor
          );
        })
      )
      .subscribe(() => {
        if (window.innerWidth < 1200) {
          this._closeMenu();
        }
      });

    // context-menu가 보일 때 헤더와 푸터 닫기
    this.eventBusService
      .on('ContextMenuComponent:visibilityChange')
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe(({ isVisible }) => {
        if (isVisible && this._isMenuOpened) {
          this._closeMenu();
        }
      });

    merge(
      this.eventBusService.on('ContentsComponent:settingsChanged'),
      this.eventBusService.on('ContentsComponent:itemLoad'),
      this.eventBusService.on('ContentsComponent:highlightClick'),
      this.eventBusService.on('ContentsComponent:zoomScaleChange'),
      this.eventBusService.on('ContentsComponent:select')
    )
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe(() => {
        this._rightPanelComp.memoListComp?.close();
        this._memoPopupComp.close();
        this._staticMemoPopupComp.close();
      });

    merge(
      this.eventBusService.on('ContentsComponent:pageClick2'),
      this.eventBusService.on('ContentsComponent:pdfPageScrolled')
    )
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe(() => {
        this._memoPopupComp.close();
        this._staticMemoPopupComp.close();
      });

    this.eventBusService
      .on('HeaderComponent:menuClick')
      .pipe(
        takeUntil(this.unsubscribeBroadcast),
        filter(({ menu }) => menu === HeaderMenuItem.GroupMemos)
      )
      .subscribe(() => {
        this._skipHighlightsOnNextPanelOpen = true;
        this._suppressHighlightOnNextModeSwitch = true;
        this._contentsComp?.suppressNextRefererHighlight();
      });

    // 두 페이지 보기 모드를 한 페이지 보기 모드로 전환하기 위한 이벤트 버스 구독
    merge(
      this.eventBusService.on(
        'AnnotationSettingsFabComponent:anotationsButtonClick'
      ),
      this.eventBusService.on('ContentsComponent:memoIndicatorClick'),
      this.eventBusService.on(
        'ContentsComponent:commentaryPreviewMemoIndicatorClick'
      ),
      // memo indicator 클릭 흐름에서 패널이 직접 열리는 경우도 포함
      this.eventBusService.on('RightPanelComponent:openRightPanelTab'),
      // GroupMemos 메뉴 클릭 이벤트 추가
      this.eventBusService
        .on('HeaderComponent:menuClick')
        .pipe(filter(({ menu }) => menu === HeaderMenuItem.GroupMemos)),
      this.eventBusService
        .on('FooterComponent:menuClick')
        .pipe(filter(({ menu }) => menu === FooterMenuItem.GroupMemos))
    )
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe(() => {
        if (this._contentsComp.getCurrentSettings()) {
          const message = this._readingGroup
            ? '댓글 보기를 위해 한 페이지 보기 모드로 변경합니다.'
            : '‘내 기록 보기’를 위해 한 페이지 보기 모드로 변경합니다.';
          this._toastService.open(message);

          this._wasMultiColumnBeforeRightPanelOpen = true;
          const applyHighlights = !this._suppressHighlightOnNextModeSwitch;
          this._suppressHighlightOnNextModeSwitch = false;
          this._contentsComp?.suppressNextRefererHighlight();
          this._contentsComp?._onSettingsChanged(
            'multiColumn',
            false,
            applyHighlights
          );
        }

        if (window.innerWidth < 1200) {
          this._closeMenu();
        }
      });

    this.eventBusService
      .on('ContentsComponent:commentaryPreviewMemoIndicatorClick')
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe(() => {
        this._rightPanelComp.memoListComp?.close();
        this._memoPopupComp.close();
      });

    this.eventBusService
      .on('ContentsComponent:memoIndicatorClick')
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe(() => {
        this._staticMemoPopupComp.close();
      });

    this.eventBusService
      .on('ContentsComponent:pageInfoChange')
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe((event) => {
        this._onPageInfoChange(event);
      });

    // 바텀 시트가 열릴 때 33% 높이로 초기화 (아래에서 올라오는 애니메이션)
    this.eventBusService
      .on('RightPanelComponent:opened')
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe(() => {
        this._userClose = false;

        const applyHighlights = !this._skipHighlightsOnNextPanelOpen;
        this._skipHighlightsOnNextPanelOpen = false;

        if (this._rightSidebar?.nativeElement) {
          this._forceResize(applyHighlights);
          // 초기화가 완료되지 않았으면 초기화 로직에서 처리하도록 함
          if (!this._isInitialized) {
            return;
          }

          // 현재 높이 확인 - 이미 열려있는 경우(탭 변경)에는 높이를 재설정하지 않음
          const currentHeight =
            this._rightSidebar.nativeElement.offsetHeight ||
            this._rightSidebar.nativeElement.clientHeight ||
            parseInt(
              getComputedStyle(this._rightSidebar.nativeElement).height,
              10
            ) ||
            this._bottomSheetHeight;

          // 높이가 0이거나 거의 0인 경우에만 초기화 (처음 열릴 때만)
          if (currentHeight <= 10) {
            // 다음 프레임에서 실행하여 DOM이 완전히 렌더링된 후 높이 설정
            setTimeout(() => {
              this._openBottomSheetWithAnimation(applyHighlights);
            }, 0);
          } else {
            // 이미 열려있으면 뷰어 높이만 업데이트
            this._updateViewerHeight();
          }
        }
      });

    // right-panel이 닫힐 때 두 페이지보기 모드 복원
    this.eventBusService
      .on('RightPanelComponent:closed')
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe(() => {
        this._userClose = true;
        // 이전에 두 페이지보기 모드였고, 현재 한 페이지보기 모드라면 복원
        if (
          this._wasMultiColumnBeforeRightPanelOpen === true &&
          !this._contentsComp?.getCurrentSettings()
        ) {
          this._contentsComp?._onSettingsChanged('multiColumn', true);
          this._toastService.open('두 페이지 보기 모드로 돌아갑니다.');
          this._wasMultiColumnBeforeRightPanelOpen = false;
        }

        // 상태 초기화
        this._wasMultiColumnBeforeRightPanelOpen = undefined;
        this._forceResize(false);
        this._updateViewerHeight(100);

        // 댓글 입력 중 알림 팝업 숨김
        this.isCommentInputActive = false;
        this.selectedCommentText = '';
        // 답글 입력 중 알림 팝업 숨김
        this.isReplyInputActive = false;
        this.selectedReplyNickname = '';
      });

    this.eventBusService
      .on('ReadingModeDialogComponent:groupChanged')
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe((event) => {
        // 한페이지 보기 모드로 전환
        // 오른쪽 패널이 열려있는지 여부
        if (this._rightSidebarContainerComp?.isOpened?.('right')) {
          this._contentsComp?._onSettingsChanged('multiColumn', false);
        }

        const entered$ =
          event.entered != null
            ? this._readingGroupAPIService.getById(event.entered)
            : of(null);
        const exited$ =
          event.exited != null
            ? this._readingGroupAPIService.getById(event.exited)
            : of(null);

        zip(entered$, exited$).subscribe(([entered, exited]) => {
          if (this.book) {
            this._callEnterAmplitudeEvent(entered, this.book);
          }
          this._callExitAmplitudeEvent(exited);
        });
      });

    if (this.isMobile) {
      this._updateMaxHeight();
      // _updateBottomSheetHeight()는 제거하고, 초기화는 _checkInitialBottomSheetState()에서만 처리
      window.addEventListener('resize', () => {
        if (this._userClose) {
          return;
        }
        this._updateMaxHeight();
        this._updateBottomSheetHeight();
      });

      // RightPanelComponent의 close 메서드를 래핑하여 애니메이션 적용
      this._wrapRightPanelCloseMethod();
    }

    // 댓글 입력 중 알림 팝업 관련 이벤트 구독
    this.eventBusService
      .on('ContentsComponent:commentClick')
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe(({ annotation }) => {
        if (annotation?.text) {
          this.isCommentInputActive = true;
          this.selectedCommentText = annotation.text;
        }
      });

    // 댓글 등록 성공 시 팝업 숨김
    this.eventBusService
      .on('ContentsComponent:memoUpdated')
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe(() => {
        this.isCommentInputActive = false;
        this.selectedCommentText = '';
      });

    // 댓글 입력 취소 시 팝업 숨김 (새로운 텍스트 선택 시)
    this.eventBusService
      .on('ContentsComponent:select')
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe(() => {
        if (this.isCommentInputActive) {
          this.isCommentInputActive = false;
          this.selectedCommentText = '';
        }
        if (this.isReplyInputActive) {
          this.isReplyInputActive = false;
          this.selectedReplyNickname = '';
        }
      });

    // 뷰어 클릭으로 댓글창 비활성화 될때 팝업 숨김
    this.eventBusService
      .on('ContentsComponent:pageClick')
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe(() => {
        if (this.isCommentInputActive) {
          this.isCommentInputActive = false;
          this.selectedCommentText = '';
        }
        if (this.isReplyInputActive) {
          this.isReplyInputActive = false;
          this.selectedReplyNickname = '';
        }
      });

    // 답글 입력 시작 이벤트 구독
    this.eventBusService
      .on('ReadingGroupMemoListComponent:replyInputStarted' as any)
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe((data: any) => {
        if (data?.nickname) {
          this.isReplyInputActive = true;
          this.selectedReplyNickname = data.nickname;
        }
      });

    // 답글 등록 성공 시 팝업 숨김
    this.eventBusService
      .on('ReadingGroupMemoListComponent:replyCommentAdded')
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe(() => {
        this.isReplyInputActive = false;
        this.selectedReplyNickname = '';
      });

    // 답글 입력 취소 시 팝업 숨김
    this.eventBusService
      .on('ReadingGroupMemoListComponent:replyInputCancelled' as any)
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe(() => {
        this.isReplyInputActive = false;
        this.selectedReplyNickname = '';
      });

    // 댓글 입력 취소 시 팝업 숨김
    this.eventBusService
      .on('ContentsComponent:commentInputCancelled' as any)
      .pipe(takeUntil(this.unsubscribeBroadcast))
      .subscribe(() => {
        this.isCommentInputActive = false;
        this.selectedCommentText = '';
      });

    // onWindowResize() 호출 제거 - 초기화는 ngAfterViewInit에서 처리
    // this.onWindowResize();
  }

  // ngAfterViewInit 추가
  ngAfterViewInit(): void {
    // ViewChild가 초기화된 후 초기 상태 확인 및 뷰어 높이 업데이트
    if (this.isMobile && !this._isInitialized && !this._isInitializing) {
      this._isInitializing = true;
      // 다음 프레임에서 실행하여 DOM이 완전히 렌더링된 후 높이 설정
      setTimeout(() => {
        this._checkInitialBottomSheetState();
        this._isInitialized = true;
        this._isInitializing = false;
      }, 0);
    }

    // 데스크탑에서 초기 상태를 닫힌 상태로 설정
    if (window.innerWidth >= 1200) {
      // 헤더와 푸터가 초기에 열려있다면 닫기
      if (this._sidebarTop?.nativeElement?.classList.contains('active')) {
        this._sidebarTop.nativeElement.classList.remove('active');
      }
      if (
        this._sidebarDesktopBottom?.nativeElement?.classList.contains('active')
      ) {
        this._sidebarDesktopBottom.nativeElement.classList.remove('active');
      }
      this._isMenuOpened = false;
    }
  }

  ngOnDestroy(): void {
    if (this.isMobile) {
      window.removeEventListener('resize', () => this._updateMaxHeight());
    }

    document.body.classList.remove('viewer-fixed');
    document.body.removeAttribute('data-theme');

    this._callExitAmplitudeEvent(this._readingGroup);

    this.intervalLoggerService.stop();

    this.unsubscribeBroadcast.next();
    this.unsubscribeBroadcast.complete();

    // intervalSub가 있는지 판단
    if (!this.intervalSub) {
      return;
    }
    this.intervalSub!.unsubscribe(); // x타이머 중지
  }

  // 초기 바텀시트 상태 확인 및 뷰어 높이 조정
  private _checkInitialBottomSheetState(): void {
    if (window.innerWidth >= 1200 || !this._rightSidebar?.nativeElement) {
      return;
    }

    // 바텀시트가 열려있는지 확인
    const isOpened =
      this._rightPanelComp &&
      this._rightSidebarContainerComp?.isOpened?.('right');

    if (isOpened) {
      const currentHeight =
        this._rightSidebar.nativeElement.offsetHeight ||
        this._rightSidebar.nativeElement.clientHeight ||
        parseInt(
          getComputedStyle(this._rightSidebar.nativeElement).height,
          10
        ) ||
        0;

      // 높이가 0이거나 거의 0인 경우 초기화
      if (currentHeight <= 10) {
        this._updateBottomSheetHeight();
      } else {
        // 이미 열려있으면 현재 높이로 뷰어 높이 업데이트
        this._bottomSheetHeight = currentHeight;
        this._updateViewerHeight();
      }
    } else {
      // 닫혀있으면 뷰어 높이를 100%로 설정
      this._updateViewerHeight();
    }
  }

  // 티켓에 대한 카운트다운 계산해주는 함수
  private updateCountdown(expiredAt: any, bid?: string): void {
    const currentDate = new Date();
    const expiredDate = new Date(expiredAt);
    const diff = expiredDate.getTime() - currentDate.getTime();

    // 만료 됐을때 종료 및 상태값 변경
    if (diff <= 0) {
      this.isExpired = true;
      if (this.intervalSub) {
        this.intervalSub.unsubscribe(); // x타이머 중지
      }
      alert('구독 시간이 만료됐습니다.');
      location.href = `https://buk.io/@${bid}`;
      return;
    }

    const seconds = Math.floor(diff / 1000);
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    // 두 자리 숫자로 포맷팅
    const pad = (n: number): string => {
      return n.toString().padStart(2, '0');
    };

    this.expiredTime = `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
  }

  private _updateMaxHeight(): void {
    const satValue = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--sat') ||
        '0',
      10
    );
    this._maxHeight = window.innerHeight * 0.9 - satValue;
  }

  private _getBottomSheetHeightPercent(): number {
    if (!this._rightSidebar?.nativeElement || window.innerWidth >= 1200) {
      return 0;
    }

    const bodyHeight =
      document.body.clientHeight ||
      document.body.offsetHeight ||
      window.innerHeight;

    if (bodyHeight === 0) {
      return 0;
    }

    const currentHeight = this._bottomSheetHeight;
    const height33 = this._getHeight33Percent();
    const height50 = this._getHeight50Percent();
    const height100 = this._getHeight100Percent();

    // 높이에 따라 가장 가까운 퍼센트 반환
    if (currentHeight <= 0) {
      return 0;
    } else if (
      Math.abs(currentHeight - height33) < Math.abs(currentHeight - height50)
    ) {
      if (Math.abs(currentHeight - height33) < Math.abs(currentHeight - 0)) {
        return 33;
      }
      return 0;
    } else if (
      Math.abs(currentHeight - height50) < Math.abs(currentHeight - height100)
    ) {
      return 50;
    } else {
      return 100;
    }
  }

  private _getViewerHeightPercent(bottomSheetPercent: number): number {
    // 바텀시트 높이에 따라 뷰어 높이 계산
    if (bottomSheetPercent === 0) {
      return 100; // 바텀시트 0% → 뷰어 100%
    } else if (bottomSheetPercent === 33) {
      return 67; // 바텀시트 33% → 뷰어 67%
    } else if (bottomSheetPercent === 50) {
      return 50; // 바텀시트 50% → 뷰어 50%
    } else if (bottomSheetPercent === 100) {
      return 50; // 바텀시트 100% → 뷰어 50% (그대로 유지)
    }
    return 100; // 기본값
  }

  private _updateViewerHeight(heightPercent: number = 0): void {
    // 모바일에서만 (1200px 미만) 동작
    if (window.innerWidth >= 1200) {
      // 데스크톱에서는 뷰어 높이를 100%로 유지
      const viewerWrapper = this.element.nativeElement.querySelector(
        '.sidebar-outer-wrapper'
      );
      if (viewerWrapper) {
        viewerWrapper.style.height = '100%';
        viewerWrapper.style.flex = '1 1 auto'; // flex 속성 복원
      }
      // 데스크탑 모드에서는 rightSidebar의 height도 리셋
      if (this._rightSidebar?.nativeElement) {
        this._rightSidebar.nativeElement.style.height = '';
        this._bottomSheetHeight = 0;
      }
      return;
    }

    // 실제 DOM 요소의 높이를 직접 확인
    if (this._rightSidebar?.nativeElement) {
      const actualHeight =
        this._rightSidebar.nativeElement.offsetHeight ||
        this._rightSidebar.nativeElement.clientHeight ||
        parseInt(
          getComputedStyle(this._rightSidebar.nativeElement).height,
          10
        ) ||
        0;

      const bodyHeight =
        document.body.clientHeight ||
        document.body.offsetHeight ||
        window.innerHeight;

      // 실제 높이가 최대 높이(90%) 이상이면 최대 높이로 간주
      if (bodyHeight > 0 && actualHeight >= this._maxHeight) {
        return;
      }
    }

    const bottomSheetPercent = this._getBottomSheetHeightPercent();

    // 패널 높이가 100%일 때는 뷰어 높이 업데이트하지 않음
    if (bottomSheetPercent === 100) {
      return;
    }

    const viewerHeightPercent =
      this._getViewerHeightPercent(bottomSheetPercent);

    // sidebar-outer-wrapper의 높이 조정
    const viewerWrapper = this.element.nativeElement.querySelector(
      '.sidebar-outer-wrapper'
    );
    if (viewerWrapper) {
      const bodyHeight =
        document.body.clientHeight ||
        document.body.offsetHeight ||
        window.innerHeight;
      const viewerHeight = (bodyHeight * viewerHeightPercent) / 100;

      // flex 속성을 none으로 설정하여 flex: 1의 영향을 제거
      viewerWrapper.style.flex = 'none';
      viewerWrapper.style.height =
        heightPercent > 0 ? `${heightPercent}%` : `${viewerHeight}px`;
      viewerWrapper.style.flexShrink = '0';
      viewerWrapper.style.flexGrow = '0';
    }
  }

  private _updateBottomSheetHeight(): void {
    const satValue = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--sat') ||
        '0',
      10
    );
    const bodyHeight =
      document.body.clientHeight ||
      document.body.offsetHeight ||
      window.innerHeight;
    this._bottomSheetHeight = (bodyHeight - satValue) / 3;

    // 최소/최대 높이 제한 적용
    this._bottomSheetHeight = Math.max(
      this._minHeight,
      Math.min(this._maxHeight, this._bottomSheetHeight)
    );

    // 초기 높이 적용
    if (this._rightSidebar?.nativeElement) {
      this._rightSidebar.nativeElement.style.height = `${this._bottomSheetHeight}px`;
    }

    // 뷰어 높이 업데이트
    this._updateViewerHeight();
  }

  private _getHeight33Percent(): number {
    const satValue = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--sat') ||
        '0',
      10
    );
    const bodyHeight =
      document.body.clientHeight ||
      document.body.offsetHeight ||
      window.innerHeight;
    const height33 = (bodyHeight - satValue) / 3;
    return Math.max(this._minHeight, Math.min(this._maxHeight, height33));
  }

  private _getHeight50Percent(): number {
    const satValue = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--sat') ||
        '0',
      10
    );
    const bodyHeight =
      document.body.clientHeight ||
      document.body.offsetHeight ||
      window.innerHeight;
    const height50 = (bodyHeight - satValue) / 2;
    return Math.max(this._minHeight, Math.min(this._maxHeight, height50));
  }

  private _getHeight100Percent(): number {
    // 100%는 body 높이에서 --sat 값을 뺀 높이
    const satValue = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--sat') ||
        '0',
      10
    );
    const bodyHeight =
      document.body.clientHeight ||
      document.body.offsetHeight ||
      window.innerHeight;
    return bodyHeight - satValue;
  }

  private _getSnapHeight(currentHeight: number): number | null {
    const height33 = this._getHeight33Percent();
    const height50 = this._getHeight50Percent();
    const height100 = this._getHeight100Percent();

    // 100%보다 큰 경우는 모두 100%로
    if (currentHeight > height100) {
      return height100;
    }

    // 50%와 100% 사이의 중간점 계산
    const midPoint50_100 = (height50 + height100) / 2;

    // 50%와 100% 사이인 경우
    if (currentHeight > height50) {
      if (currentHeight >= midPoint50_100) {
        return height100; // 중간보다 조금 넘으면 100%로
      } else {
        return height50; // 중간보다 조금 적으면 50%로
      }
    }

    // 33%와 50% 사이의 중간점 계산
    const midPoint33_50 = (height33 + height50) / 2;

    // 33%와 50% 사이인 경우
    if (currentHeight > height33) {
      if (currentHeight >= midPoint33_50) {
        return height50; // 중간보다 조금 넘으면 50%로
      } else {
        return height33; // 중간보다 조금 적으면 33%로
      }
    }

    // 0%와 33% 사이의 중간점 계산
    const midPoint0_33 = height33 / 2;

    // 0%와 33% 사이인 경우
    if (currentHeight >= midPoint0_33) {
      return height33; // 중간보다 조금 넘으면 33%로
    } else {
      return null; // 중간보다 조금 적으면 0%로 (닫기)
    }
  }

  private _snapToNearestHeight(): void {
    const snapHeight = this._getSnapHeight(this._bottomSheetHeight);

    // 0%에 가까워지면 바텀 시트 닫기 (애니메이션 후)
    if (snapHeight === null) {
      this._closeBottomSheetWithAnimation();
      // 애니메이션 완료 후 실제로 닫기
      setTimeout(() => {
        this._rightPanelComp.close();
        this._updateViewerHeight(100); // 뷰어 높이 업데이트
        this._rightPanelComp.readingGroupMemoListComp?._disableCommentInput();
        this._forceResize(false);
      }, 300);
      return;
    }

    const height100 = this._getHeight100Percent();
    const isFullHeight = Math.abs(snapHeight - height100) < 1; // 1px 오차 허용

    if (this._rightSidebar?.nativeElement) {
      // CSS transition을 사용한 부드러운 애니메이션
      this._rightSidebar.nativeElement.style.transition =
        'height 0.3s ease-out';
      this._rightSidebar.nativeElement.style.height = `${snapHeight}px`;
      this._bottomSheetHeight = snapHeight;

      // 100%일 때 클래스 추가/제거
      if (isFullHeight) {
        this._rightSidebar.nativeElement.classList.add('full-height');
      } else {
        this._rightSidebar.nativeElement.classList.remove('full-height');
      }

      // 애니메이션 완료 후 transition 제거 (드래그 시 즉시 반응하도록)
      setTimeout(() => {
        if (this._rightSidebar?.nativeElement) {
          this._rightSidebar.nativeElement.style.transition = '';
        }
        // 뷰어 높이 업데이트
        this._updateViewerHeight();
        this._rightPanelComp.readingGroupMemoListComp?._disableCommentInput();
        this._forceResize();
      }, 300);
    }
  }

  private _wrapRightPanelCloseMethod(): void {
    // ViewChild가 초기화된 후에 실행되도록 setTimeout 사용
    setTimeout(() => {
      if (!this._rightPanelComp) {
        return;
      }

      const originalClose = this._rightPanelComp.close.bind(
        this._rightPanelComp
      );

      this._rightPanelComp.close = (emitEvent: boolean = true): void => {
        if (this.isMobile && this._rightSidebar?.nativeElement) {
          const currentHeight =
            this._rightSidebar.nativeElement.offsetHeight ||
            this._rightSidebar.nativeElement.clientHeight ||
            parseInt(
              getComputedStyle(this._rightSidebar.nativeElement).height,
              10
            ) ||
            this._bottomSheetHeight;

          // 높이가 0보다 크면 애니메이션 적용 후 닫기
          if (currentHeight > 0) {
            this._closeBottomSheetWithAnimation();
            // 애니메이션 완료 후 실제로 닫기
            setTimeout(() => {
              originalClose(emitEvent);
              this._forceResize(false);
            }, 500);
            return;
          }
        }

        // 애니메이션이 필요 없거나 모바일이 아닌 경우 원래 메서드 호출
        originalClose(emitEvent);
      };
    }, 0);
  }

  private _openBottomSheetWithAnimation(applyHighlights: boolean = true): void {
    if (window.innerWidth >= 1200 || !this._rightSidebar?.nativeElement) {
      return;
    }

    const satValue = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--sat') ||
        '0',
      10
    );
    const bodyHeight =
      document.body.clientHeight ||
      document.body.offsetHeight ||
      window.innerHeight;
    const targetHeight = (bodyHeight - satValue) / 3;

    // 최소/최대 높이 제한 적용
    const finalHeight = Math.max(
      this._minHeight,
      Math.min(this._maxHeight, targetHeight)
    );

    // 먼저 높이를 0으로 설정 (1200px 미만일 때만)
    this._rightSidebar.nativeElement.style.height = '0px';
    this._bottomSheetHeight = 0;

    // 다음 프레임에서 33% 높이로 애니메이션
    requestAnimationFrame(() => {
      if (this._rightSidebar?.nativeElement) {
        this._rightSidebar.nativeElement.style.transition =
          'height 0.3s ease-out';
        this._rightSidebar.nativeElement.style.height = `${finalHeight}px`;
        this._bottomSheetHeight = finalHeight;

        // 애니메이션 완료 후 transition 제거 (드래그 시 즉시 반응하도록)
        setTimeout(() => {
          if (this._rightSidebar?.nativeElement) {
            this._rightSidebar.nativeElement.style.transition = '';
          }
          // 뷰어 높이 업데이트
          this._updateViewerHeight();
          this._forceResize(applyHighlights);
        }, 300);
      }
    });
  }

  private _closeBottomSheetWithAnimation(): void {
    if (window.innerWidth >= 1200 || !this._rightSidebar?.nativeElement) {
      // 애니메이션 없이 바로 닫기 (이미 닫힌 상태)
      return;
    }

    // 높이를 0으로 애니메이션
    this._rightSidebar.nativeElement.style.transition = 'height 0.3s ease-out';
    this._rightSidebar.nativeElement.style.height = '0px';
    this._bottomSheetHeight = 0;

    // 애니메이션 완료 후 transition 제거
    setTimeout(() => {
      if (this._rightSidebar?.nativeElement) {
        this._rightSidebar.nativeElement.style.transition = '';
      }
      // 뷰어 높이 업데이트
      this._updateViewerHeight();
    }, 300);
  }

  onHandleTouchStart(event: TouchEvent): void {
    this._isDragging = true;
    this._startY = event.touches[0].clientY;

    // 실제 DOM 요소의 현재 높이를 읽어서 시작 높이로 설정
    if (this._rightSidebar?.nativeElement) {
      const currentHeight =
        this._rightSidebar.nativeElement.offsetHeight ||
        this._rightSidebar.nativeElement.clientHeight ||
        parseInt(
          getComputedStyle(this._rightSidebar.nativeElement).height,
          10
        ) ||
        this._bottomSheetHeight;
      this._startHeight = currentHeight;
      this._bottomSheetHeight = currentHeight; // 동기화
    } else {
      this._startHeight = this._bottomSheetHeight;
    }

    // 드래그 시작 시 transition 제거 (즉시 반응하도록)
    if (this._rightSidebar?.nativeElement) {
      this._rightSidebar.nativeElement.style.transition = '';
    }

    // 기본 터치 동작 방지
    event.preventDefault();
  }

  private _forceResize(applyHighlights: boolean = true): void {
    if (this._contentsComp?._viewerComp?.triggerResize) {
      this._contentsComp._viewerComp.triggerResize(applyHighlights);
    }
  }

  onHandleTouchMove(event: TouchEvent): void {
    event.stopPropagation();
    if (!this._isDragging || !this._rightSidebar) {
      return;
    }

    const currentY = event.touches[0].clientY;
    const deltaY = this._startY - currentY; // 위로 드래그하면 양수
    const newHeight = this._startHeight + deltaY;

    // 드래그 중에는 최대 높이(90%)까지 허용, 최소는 0까지 허용
    const maxDragHeight = this._maxHeight;

    // 높이 제한 (최소는 0까지 허용)
    const clampedHeight = Math.max(0, Math.min(maxDragHeight, newHeight));
    this._bottomSheetHeight = clampedHeight;

    // 즉시 높이 적용
    if (this._rightSidebar.nativeElement) {
      this._rightSidebar.nativeElement.style.height = `${this._bottomSheetHeight}px`;

      // 드래그 중에는 max-height와 border-radius 복원 (스냅 시에만 제거)
      this._rightSidebar.nativeElement.style.maxHeight = '';
      this._rightSidebar.nativeElement.style.borderRadius = '';
    }

    event.preventDefault();
  }

  onHandleTouchEnd(event: TouchEvent): void {
    if (!this._isDragging) {
      return;
    }

    this._isDragging = false;

    // 33% 또는 50% 위치로 자동 스냅
    this._snapToNearestHeight();
  }

  _onVerticalSidebarOpened(): void {
    this._isMenuOpened = true;
    this.menuOpenedChange.emit(true);
  }

  _onVerticalSidebarClosed(): void {
    this._isMenuOpened = false;
    this.menuOpenedChange.emit(false);
  }

  _onHorizontalSidebarOpened(): void {
    this._isSidebarOpened = true;
  }

  _onHorizontalSidebarClosed(): void {
    this._isSidebarOpened = false;
  }

  _onPageInfoChange(
    event: PageInfoChangeEvent & {
      address: Address;
    }
  ): void {
    if (!this.book) {
      return;
    }

    let percent;

    if (this.book.meta.type === BookType.PDF) {
      percent =
        (this.book.getIndexOfItem(event.address.iid ?? this.book.items[0].iid) +
          1) /
        this.book.items.length;
    } else if (this.book.meta.type === BookType.Document) {
      percent = event.page / event.pageCount;
    } else {
      percent = this.book.getPagePercent(
        event.address.iid ?? this.book.items[0].iid,
        event.page,
        event.pageCount
      );
    }

    this._completionRate = roundTo(4, percent);
  }

  private _openMenu(): void {
    this._sidebarTop.nativeElement.classList.add('animating');
    this._sidebarTop.nativeElement.classList.add('active');

    if (window.innerWidth >= 1200) {
      this._sidebarDesktopBottom.nativeElement.classList.add('animating');
      this._sidebarDesktopBottom.nativeElement.classList.add('active');
    } else {
      this._sidebarMobileBottom.nativeElement.classList.add('animating');
      this._sidebarMobileBottom.nativeElement.classList.add('active');
    }

    this.eventBusService.fire('SidebarContainerComponent:opened', {
      position: 'top',
    });
    this.eventBusService.fire('SidebarContainerComponent:opened', {
      position: 'bottom',
    });

    setTimeout(() => {
      // sidebars 객체 대신 직접 참조 사용
      this._sidebarTop.nativeElement.classList.remove('animating');
      if (window.innerWidth >= 1200) {
        this._sidebarDesktopBottom.nativeElement.classList.remove('animating');
      } else {
        this._sidebarMobileBottom.nativeElement.classList.remove('animating');
      }
    }, 400);

    this._onVerticalSidebarOpened();
  }

  private _closeMenu(): void {
    this._sidebarTop.nativeElement.classList.add('animating');
    this._sidebarTop.nativeElement.classList.remove('active');

    if (window.innerWidth >= 1200) {
      this._sidebarDesktopBottom.nativeElement.classList.add('animating');
      this._sidebarDesktopBottom.nativeElement.classList.remove('active');
    } else {
      this._sidebarMobileBottom.nativeElement.classList.add('animating');
      this._sidebarMobileBottom.nativeElement.classList.remove('active');
    }

    this.eventBusService.fire('SidebarContainerComponent:closed', {
      position: 'top',
    });
    this.eventBusService.fire('SidebarContainerComponent:closed', {
      position: 'bottom',
    });

    setTimeout(() => {
      this.sidebars['top']?.classList.remove('animating');
      this.sidebars['bottom']?.classList.remove('animating');
    }, 400);

    this._onVerticalSidebarClosed();
  }

  _selectCommentary(id: number): void {
    this._selectedCommentaryAuthor = this.book?.meta.commentaries.find(
      (commentary) => commentary.id === id
    )?.author;
  }

  _callEnterAmplitudeEvent(
    readingGroup: ReadingGroupWithMyActivityStats | null,
    book: Book
  ): void {
    if (!readingGroup) {
      this._amplitudeService.logEvent('enter_viewer_single_mode', {
        purchase_at: book.permission.purchased_at,
        category: book.meta.category ? book.meta.category[0].name : null,
        bid: book.meta.bid,
        is_preview: !book.isOwnedByUser,
      });
    } else {
      this._amplitudeService.logEvent('enter_viewer_together_mode', {
        purchase_at: book.permission.purchased_at,
        category: book.meta.category ? book.meta.category[0].name : null,
        bid: book.meta.bid,
        book_title: book.meta.title,
        group_name: readingGroup.name,
        group_type: readingGroup.type,
        group_member_count: readingGroup.member_count,
        group_leader: readingGroup.leader_nickname,
        memo_count: readingGroup.memo_count,
        highlight_count: readingGroup.highlight_count,
        my_memo_count: readingGroup.my_memo_count,
        my_memo_like_count: readingGroup.my_memo_like_count,
        my_memo_copy_count: readingGroup.my_memo_copy_count,
        my_highlights_count: readingGroup.my_highlights_count,
        is_preview: !book.isOwnedByUser,
      });
    }
  }

  _callExitAmplitudeEvent(
    readingGroup: ReadingGroupWithMyActivityStats | null
  ): void {
    if (!this.book) {
      return;
    }

    if (!readingGroup) {
      this._amplitudeService.logEvent('exit_viewer_single_mode', {
        completion_rate: this._completionRate,
        purchase_at: this.book!.permission.purchased_at,
        category: this.book.meta.category
          ? this.book.meta.category[0].name
          : null,

        bid: this.book!.meta.bid,
        commentary_name: this._selectedCommentaryAuthor,
        is_preview: !this.book.isOwnedByUser,
      });
    } else {
      this._amplitudeService.logEvent('exit_viewer_together_mode', {
        completion_rate: this._completionRate,
        purchase_at: this.book!.permission.purchased_at,
        category: this.book.meta.category
          ? this.book.meta.category[0].name
          : null,
        bid: this.book!.meta.bid,
        book_title: this.book!.meta.title,
        group_name: readingGroup.name,
        group_type: readingGroup.type,
        group_member_count: readingGroup.member_count,
        group_leader: readingGroup.leader_nickname,
        memo_count: readingGroup.memo_count,
        highlight_count: readingGroup.highlight_count,
        my_memo_count: readingGroup.my_memo_count,
        my_memo_like_count: readingGroup.my_memo_like_count,
        my_memo_copy_count: readingGroup.my_memo_copy_count,
        my_highlights_count: readingGroup.my_highlights_count,
        is_preview: !this.book.isOwnedByUser,
      });
    }
  }

  // 자동 그룹 가입 시도 메서드
  private _tryAutoJoinGroup(
    book: Book,
    readingGroup: ReadingGroupWithMyActivityStats | null
  ): void {
    // 이미 시도했거나 gId가 없으면 리턴
    if (this._hasAttemptedAutoJoin || !this._groupId) {
      return;
    }

    // UserService가 없으면 리턴
    if (!this._userService) {
      return;
    }

    // 이미 그룹에 가입되어 있으면 리턴
    if (
      readingGroup &&
      (readingGroup.status === ReadingGroupJoinStatus.joined ||
        readingGroup.status === ReadingGroupJoinStatus.owned)
    ) {
      return;
    }

    // 사용자 정보 확인
    this._userService.user
      .pipe(takeUntil(this.unsubscribeBroadcast), first())
      .subscribe((user) => {
        // 로그인되어 있고 책을 구매한 경우에만 자동 가입 시도
        if (user && book.isOwnedByUser) {
          this._hasAttemptedAutoJoin = true;

          this._readingGroupsAPIService.join(Number(this._groupId)).subscribe({
            next: () => {
              // 그룹 가입 성공 시 그룹 정보 새로고침
              this._readingGroupsAPIService
                .getById(Number(this._groupId))
                .subscribe((updatedGroup) => {
                  // 그룹 서비스에 업데이트된 그룹 정보 전달
                  // 필요시 추가 처리
                });
            },
            error: (error) => {
              // 에러 발생 시 무시 (이미 가입되어 있거나, 비공개 그룹 등)
              // 사용자에게 알림을 표시하지 않음
              console.log('자동 그룹 가입 실패:', error);
            },
          });
        }
      });
  }

  // 필기모드 on 클릭시에 자동으로 top과 bottom 메뉴를 닫도록 함
  onDrawingModeToggled(event: boolean): void {
    if (event && window.innerWidth < 1200) {
      this._closeMenu();
    }
  }

  _onNoteUrlClicked(url: string): void {
    // this.router.navigate([url]);
    this.eventBusService.fire('AnnotationsComponent:itemClick', {
      url,
    });
  }
}
